import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { useBrand } from "@/lib/brand";
import { PageHeader, Panel, StatusBadge, PageLoader, Btn, EmptyState, SectionTitle, brandName } from "@/lib/ui";
import { UploadCloud, Sparkles, Download, FileText } from "lucide-react";

const TONE = { STORED: "muted", PENDING_CLASSIFICATION: "warn", CLASSIFIED: "ok" };

export default function Uploads() {
  const api = useApi();
  const qc = useQueryClient();
  const { brand } = useBrand();
  const fileRef = useRef(null);
  const [category, setCategory] = useState("general");
  const { data: uploads = [], isLoading } = useQuery({ queryKey: ["uploads", brand], queryFn: () => api.get(`/uploads?brand_id=${brand}`) });

  const upload = useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("brand_id", brand);
      fd.append("category", category);
      return api.upload("/uploads", fd);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["uploads"] }); toast.success("File uploaded & stored"); },
    onError: (e) => toast.error(e.message),
  });
  const classify = useMutation({
    mutationFn: (id) => api.post(`/uploads/${id}/classify`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["uploads"] }); toast.success("AI classification complete"); },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader kicker={`${brandName(brand)} · Content`} title="Upload Centre">
        <input type="text" value={category} onChange={(e) => setCategory(e.target.value)}
          className="h-9 rounded-md border border-border bg-surface px-3 text-sm w-32" placeholder="category" data-testid="upload-category" />
        <input ref={fileRef} type="file" hidden onChange={(e) => e.target.files[0] && upload.mutate(e.target.files[0])} data-testid="upload-file-input" />
        <Btn onClick={() => fileRef.current?.click()} disabled={upload.isPending} data-testid="upload-btn">
          <UploadCloud className="h-4 w-4" /> {upload.isPending ? "Uploading…" : "Upload File"}
        </Btn>
      </PageHeader>

      <div className="text-xs text-muted-foreground mb-4 font-mono">
        Supported: XLSX · CSV · PDF · DOCX · PPTX · PNG · JPG · WEBP · MP4 · MOV · MP3 · WAV · ZIP · JSON
      </div>

      {uploads.length === 0 ? (
        <EmptyState icon={UploadCloud} title="No files uploaded yet" hint="Upload a file to store it and let the AI classify it and suggest its likely use. Nothing is imported automatically without review." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {uploads.map((u) => (
            <Panel key={u.id} className="p-4">
              <div className="flex items-start justify-between">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <StatusBadge tone={TONE[u.processing_status] || "muted"}>{u.processing_status}</StatusBadge>
              </div>
              <div className="text-sm font-medium mt-2 truncate">{u.filename}</div>
              <div className="text-[11px] font-mono text-muted-foreground mt-1">
                .{u.file_type} · {(u.size / 1024).toFixed(1)} KB · {u.category}
              </div>
              {u.ai_classification && (
                <div className="mt-2 border border-ok/25 bg-ok/5 rounded-md p-2">
                  <div className="text-[10px] uppercase font-mono text-ok">{u.ai_classification.classification}</div>
                  <div className="text-xs text-foreground/80 mt-1">{u.ai_classification.suggested_use}</div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(u.tags || []).map((t) => <span key={t} className="text-[10px] border border-border rounded px-1.5">{t}</span>)}
                  </div>
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <Btn size="sm" variant="outline" onClick={() => classify.mutate(u.id)} disabled={classify.isPending} data-testid={`classify-${u.id}`}>
                  <Sparkles className="h-3.5 w-3.5" /> Classify
                </Btn>
                <Btn size="sm" variant="ghost" onClick={() => api.download(`/uploads/${u.id}/download`, u.filename)} data-testid={`download-upload-${u.id}`}>
                  <Download className="h-3.5 w-3.5" />
                </Btn>
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
